import { Config, ApiEndpoint } from '../types';

// Type for relationship entities from the backend
interface RelationshipEntity {
  id: number | string; // Support both Django IDs and Fulcrum UUIDs
  name?: string;
  entity_name?: string;
  rel_description?: string;
  thumbnail?: string;
  [key: string]: any;
}

/**
 * Safely extract entity name from relationship entity, handling backend inconsistencies
 * Backend sometimes provides 'name', sometimes 'entity_name'
 */
export const getEntityName = (entity: RelationshipEntity): string => {
  return entity.name || entity.entity_name || `Item ${entity.id}`;
};

/**
 * Extract relationship entities from both geo_entities and related_entities
 * Backend inconsistently stores geo entities in either location
 */
export const extractRelationshipEntities = (
  relationships: any,
  returnedRelationshipsKey: string
): any[] => {
  if (!relationships) return [];
  
  // Check related_entities first
  const fromRelated = relationships.related_entities?.[returnedRelationshipsKey] || [];
  
  // If found in related_entities, use that
  if (fromRelated.length > 0) {
    return fromRelated;
  }
  
  // Otherwise check geo_entities
  const fromGeo = relationships.geo_entities?.[returnedRelationshipsKey] || [];
  return fromGeo;
};

/**
 * Convert relationships to flat array of display items
 * Handles both geo_entities and related_entities, excludes media
 */
export const flattenRelationshipsForDisplay = (
  relationships: any,
  config: Config
): Array<{
  key: string;
  id: string | number;
  name: string;
  isGeo: boolean;
  category: string;
}> => {
  if (!relationships) return [];
  
  const allItems: Array<{
    key: string;
    id: string | number;
    name: string;
    isGeo: boolean;
    category: string;
  }> = [];
  
  // Process all relationship categories
  const processEntities = (entities: any, sourceKey: string, sourceType: string) => {
    if (!entities) return;
    
    Object.entries(entities).forEach(([key, entityList]) => {
      if (!Array.isArray(entityList) || entityList.length === 0) return;
      
      // Find matching endpoint
      const matchingEndpoint = config.apiEndpoints.find(ep => 
        ep.returnedRelationshipsKey === key
      );
      
             entityList.forEach((entity: RelationshipEntity) => {
         allItems.push({
           key,
           id: entity.id,
           name: getEntityName(entity),
           isGeo: matchingEndpoint?.entityType === 'geo',
           category: key
         });
       });
    });
  };
  
  // Process both sources
  processEntities(relationships.geo_entities, 'geo_entities', 'geo');
  processEntities(relationships.related_entities, 'related_entities', 'related');
  
  return allItems;
};

/**
 * Convert relationships to editable row format for RelationshipEditDialog
 * Maps from returnedRelationshipsKey to tableName
 */
export const relationshipsToEditableRow = (
  relationships: any,
  config: Config
): Record<string, (number | string)[]> => {
  if (!relationships) return {};
  
  const row: Record<string, (number | string)[]> = {};
  
  // Process all relationship categories
  const processEntities = (entities: any, sourceType: string) => {
    if (!entities) return;
    
    Object.entries(entities).forEach(([relationshipKey, entityList]) => {
      if (!Array.isArray(entityList) || entityList.length === 0) return;
      
      // Find matching endpoint to get tableName
      const matchingEndpoint = config.apiEndpoints.find(ep => 
        ep.returnedRelationshipsKey === relationshipKey
      );
      
             if (matchingEndpoint) {
         const ids = entityList.map((entity: RelationshipEntity) => {
           // Convert string IDs to numbers if possible (for Django compatibility)
           if (typeof entity.id === 'string') {
             const numericId = parseInt(entity.id, 10);
             return isNaN(numericId) ? entity.id : numericId;
           }
           return entity.id;
         });
         row[matchingEndpoint.tableName] = ids;
       }
    });
  };
  
  // Process both geo_entities and related_entities
  processEntities(relationships.geo_entities, 'geo_entities');
  processEntities(relationships.related_entities, 'related_entities');
  
  // Handle media separately (already uses correct key)
  if (relationships.media && Array.isArray(relationships.media) && relationships.media.length > 0) {
    row['media'] = relationships.media.map((entity: RelationshipEntity) => entity.id);
  }
  
  return row;
};

/**
 * Extract media items with proper name resolution
 */
export const extractMediaItems = (mediaRelationships: RelationshipEntity[]): Array<{
  id: number | string; // Support both Django IDs and Fulcrum UUIDs
  name: string;
  entity_name: string;
  entity_thumbnail: string;
  entity_description: string;
}> => {
  if (!Array.isArray(mediaRelationships)) return [];
  
  return mediaRelationships.map((media: RelationshipEntity) => {
    const mediaName = getEntityName(media);
    return {
      id: media.id,
      name: mediaName,
      entity_name: mediaName, // Keep for backward compatibility
      entity_thumbnail: media.thumbnail || '',
      entity_description: media.rel_description || ""
    };
  });
}; 